import type { ParsedLinkedWebhookLogsType } from '../../components/LinkedWebhooks/LinkedWebhooks';
import type { FormApi } from '../../form-hooks/useFormApi';
import type { ContentObjectSubmitFn } from '../../form/ContentObjectForm/ContentObjectForm';
import type {
  ContentObject,
  ContentTypeDefinition,
  Webhook,
  WebhookLog,
  WithOptionalInternalProps,
  WithoutInternalProps,
} from '../../lib/flotiq-client/types/api-response';

import * as Resizer from '@column-resizer/react';
import {
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import deepEqual from 'deep-equal';
import { unflatten } from 'dot-prop';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';
import { listWebhooks, logsWebhooks } from '../../lib/flotiq-client';
import { saveNewCTO, updateCTO } from '../../lib/flotiq-client/api-helpers';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { FormAddSecondaryColumnEvent } from '../../lib/flotiq-plugins/plugin-events/FormAddSecondaryColumnEvent';
import { FormAddSidebarPanelEvent } from '../../lib/flotiq-plugins/plugin-events/FormAddSidebarPanelEvent';
import { getSortedBy, getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import ContentObjectInformation from '../../components/ContentObjectInformation/ContentObjectInformation';
import ContentObjectVersions from '../../components/ContentObjectVersions/ContentObjectVersions';
import ContentObjectWebhooks from '../../components/ContentObjectWebhooks/ContentObjectWebhooks';
import ContentPreviewPanel from '../../components/ContentPreviewPanel/ContentPreviewPanel';
import CustomLinksPanel from '../../components/CustomLinksPanel/CustomLinksPanel';
import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import ElementFromPlugin from '../../components/ElementFromPlugin/ElementFromPlugin';
import FormEditorsIndicator from '../../components/FormEditorsIndicator/FormEditorsIndicator';
import Heading from '../../components/Heading/Heading';
import { LinkedWebhooks } from '../../components/LinkedWebhooks/LinkedWebhooks';
import Loader from '../../components/Loader/Loader';
import PlainObjectEditor from '../../components/PlainObjectEditor/PlainObjectEditor';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionButton from '../../components/Topbar/buttons/base/TopbarActionButton';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarDuplicateButton from '../../components/Topbar/buttons/TopbarDuplicateButton';
import TopbarPublishButton from '../../components/Topbar/buttons/TopbarPublishButton';
import TopbarReconnectButton from '../../components/Topbar/buttons/TopbarReconnectButton';
import TopbarSaveAndLeaveButton from '../../components/Topbar/buttons/TopbarSaveAndLeaveButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';
import TopbarStatus from '../../components/Topbar/status/TopbarStatus';
import { WebhookLogsContainer } from '../../components/WebhookLogs/WebhookLogsContainer';

import {
  useContentObject,
  useContentObjectVersions,
  useContentType,
} from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import { useBacklinks } from '../../hooks/api/useBacklinks';
import { useSpaceConstraint } from '../../hooks/api/useSpaceConstraint';
import useContentTypeRefresh from '../../hooks/useContentTypeRefresh/useContentTypeRefresh';
import { useDuplicateModal } from '../../hooks/useDuplicateModal/useDuplicateModal';
import useFirstLoading from '../../hooks/useFirstLoading';
import useObjectStatusChange from '../../hooks/useObjectStatusChange/useObjectStatusChange';
import useOnce from '../../hooks/useOnce';
import usePluginResults from '../../hooks/usePluginResults';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';
import useWebsocketForForm from '../../hooks/useWebsocket/useWebsocketForForm';
import { sendRefetchSignalToWsServer } from '../../hooks/useWebsocket/websocket';

import DirtyHandlerContext from '../../contexts/DirtyHandlerContext';
import {
  defaultButtonsProps,
  defaultTextRender,
  ModalInstanceContext,
  useModals,
} from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';
import WebsocketContext from '../../contexts/WebsocketContext';

import {
  ArrowCollapseLeftIcon,
  ArrowCollapseRightIcon,
  ArrowLeftCurvedIcon,
  BoxWithArrowIcon,
  DownloadCircleIcon,
  ResizeIcon,
  WarningIcon,
} from '../../images/shapes';

import { syncCollaborativeFieldValue } from '../../form-hooks/collaboration/syncYjs';
import ContentObjectForm from '../../form/ContentObjectForm/ContentObjectForm';
import SchedulePublicationForm from '../../form/SchedulePublicationForm/SchedulePublicationForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import PoppingPageLayout from '../../layout/PageLayout/PoppingPageLayout';
import AddWebhooksContainer from '../AddWebhooks/AddWehooksContainer';
import ContentObjectBacklinks from './ContentObjectBacklinks/ContentObjectBacklinks';
import ContentObjectPublicationSchedule from './ContentObjectPublicationSchedule/ContentObjectPublicationSchedule';
import SyncWebhookErrorLogs from './SyncWebhookLogs/SyncWebhookErrorLogs';
import { SyncWebhookErrorLogsModalContent } from './SyncWebhookLogs/SyncWebhookErrorLogsModalContent';

const versionsHookParams = {
  limit: 1000,
  page: 1,
};

type SidePanelSaveResult = [
  ContentObject,
  Record<string, string | string[]> | undefined,
  boolean,
];

export type ContentObjectFormAfterSubmit = ({
  action,
  contentObject,
  success,
  contentType,
  errors,
}: {
  action: string;
  contentObject: WithOptionalInternalProps<ContentObject>;
  success: boolean;
  contentType?: ContentTypeDefinition | null;
  errors?: Record<string, string | string[]>;
}) => void;

interface AddContentObjectContainerProps {
  /**
   * Object content type name.
   */
  contentTypeName?: string;
  /**
   * Object id to edit.
   */
  id?: string;
  /**
   * On submit action callback
   */
  onSubmit?: ContentObjectFormAfterSubmit;
  /**
   * If object is duplicating
   */
  duplicate?: boolean;
  /**
   * Test id for page
   */
  testId?: string;
  /**
   * Content Object mode
   */
  mode?: string;
}

/**
 * @emits FlotiqPlugins."flotiq.form.sidebar-panel::add"
 * @emits FlotiqPlugins."flotiq.form.secondary-column::add"
 */
const AddContentObjectContainer = ({
  contentTypeName,
  id,
  duplicate = false,
  mode = '',
  onSubmit,
  testId = '',
}: AddContentObjectContainerProps) => {
  const formId = useId();
  const navigateOnSave = useRef<boolean>(false);
  const lastPanelSaveResult = useRef<SidePanelSaveResult | null>(null);
  const { t } = useTranslation();
  const modal = useModals();
  const jwt = useToken();
  const { space, buildUrlWithSpace } = useSelectedSpace();
  const navigate = useNavigate();
  const { permissions } = use(UserContext);
  const { setDirty } = use(DirtyHandlerContext);

  const [formUniqueKey, setFormUniqueKey] = useState('');

  const isPublicationPlanningDisabled =
    getEnv('VITE_PUBLICATION_PLANNING') === 'false';

  const isSyncWebhookErrorLogsModalDisabled =
    getEnv('VITE_DISABLE_SYNC_WEBHOOK_LOGS_MODAL_IN_CO_FORM') === 'true';

  const modalInstance = use(ModalInstanceContext);
  const inModal = !!modalInstance?.resolve;

  const { navigateGrid, gridLink } = useGridNavigate(
    `objects-${contentTypeName}`,
    buildUrlWithSpace(`content-type-objects/${contentTypeName}`),
  );

  const [isResizing, toggleIsResizing] = useReducer(
    (isResizing) => !isResizing,
    false,
  );

  const [formState, setFormState] = useState<FormApi>();

  const [user, setUser] = useLocalStorageState('cms.user');

  const { reload: reloadConstraint } = useSpaceConstraint(
    'content-objects-count',
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [collaborationContentTypeUpdate, setCollaborationContentTypeUpdate] =
    useState<{ contentTypeName: string; updatedAt: string } | null>(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [syncWebhookErrorLogs, setSyncWebhookErrorLogs] = useState<
    Record<string, unknown>
  >({});

  const {
    backlinks,
    backlinkContentTypes,
    backlinkPagination,
    setBacklinksPage,
  } = useBacklinks(id, contentTypeName, duplicate);

  const versionsHookOptions = useMemo(
    () => ({ pause: !id || duplicate }),
    [duplicate, id],
  );

  const {
    data: ctoVersions,
    errors: contentVersionsErrors,
    reload: reloadContentVersions,
  } = useContentObjectVersions(
    contentTypeName,
    id,
    versionsHookParams,
    versionsHookOptions,
  );

  const [customWebhooks, setCustomWebhooks] = useState<Webhook[]>([]);
  const [linkedWebhooks, setLinkedWebhooks] = useState<Webhook[]>([]);
  const [linkedWebhooksLogs, setLinkedWebhooksLogs] =
    useState<ParsedLinkedWebhookLogsType>({});

  const logsDisabled =
    getEnv('VITE_DISABLE_WEBHOOK_LOGS_IN_CO_FORM') === 'true';

  const {
    entity: contentObject,
    isLoading: contentObjectIsLoading,
    updateEntity: updateContentObject,
    partialUpdateEntity: partialUpdateContentObject,
    deleteEntity: deleteContentObject,
    errors: contentObjectErrors,
    reload: contentObjectReload,
  } = useContentObject(contentTypeName, id);

  const onVersionChange = () => {
    contentObjectReload();
    reloadContentVersions();
  };

  const handleContentTypeDefinitionUpdate = useCallback(
    (updatedAt: string) => {
      if (!contentTypeName) return;

      setCollaborationContentTypeUpdate({ contentTypeName, updatedAt });
    },
    [contentTypeName],
  );

  const {
    yjsDocument,
    wsStatus,
    retryWsConnection,
    editors,
    awareness,
    waitForYjsSync,
  } = useWebsocketForForm(
    contentTypeName,
    id,
    onVersionChange,
    duplicate,
    handleContentTypeDefinitionUpdate,
  );

  const currentYjsValues = yjsDocument?.getMap('vals')?.toJSON();

  const isYjsDirty = useMemo(() => {
    if (wsStatus !== 'synced' || !yjsDocument || !contentObject) return false;
    if (!yjsDocument.getMap('vals').size) return false;

    const contentObjectCopy = JSON.parse(JSON.stringify(contentObject));
    delete contentObjectCopy.id;
    delete contentObjectCopy.internal;

    return !deepEqual(currentYjsValues, contentObjectCopy);
  }, [contentObject, currentYjsValues, wsStatus, yjsDocument]);

  const isDataDirty = formState?.dirty || isYjsDirty;

  const onTransitionSuccess = () => {
    if (yjsDocument)
      yjsDocument
        .getMap('formState')
        .set('publicVersion', contentObject?.internal?.publicVersion);
  };

  const { isPending: isTransiting, updateStatuses } = useObjectStatusChange(
    true,
    onTransitionSuccess,
    testId,
  );

  const editorsWithActiveField = editors?.filter(
    ({ activeField }) => !!activeField,
  );

  const fetchWebhooks = useCallback(async () => {
    if (!permissions.canCo('_webhooks')) return;
    try {
      // List webhooks with content type definitions based on content type name
      const { body, status, ok } = await listWebhooks(jwt, space, {
        filters: JSON.stringify({
          'content_type_definitions[*].content_type_definition_name': {
            type: 'contains',
            filter: `"${contentTypeName}"`,
          },
          enabled: { type: 'equals', filter: true },
        }),
      });

      // List webhooks with content type definitions that is for All (default)
      const {
        body: bodyDefault,
        status: statusDefault,
        ok: okDefault,
      } = await listWebhooks(jwt, space, {
        filters: JSON.stringify({
          content_type_definitions: {
            type: 'contains',
            filter: '[]',
          },
          enabled: { type: 'equals', filter: true },
        }),
      });

      const webhooksList = [...body.data, ...bodyDefault.data];
      const mapFromWebhooks = new Map(webhooksList.map((c) => [c.id, c]));
      const uniqueWebhookList = [...mapFromWebhooks.values()];

      const customWebhooks = uniqueWebhookList.filter((webhook: Webhook) => {
        return webhook.actions.some((element) => element.action === 'Custom');
      });

      if (!ok) {
        throw parseResponseErrors(body, status);
      }
      if (!okDefault) {
        throw parseResponseErrors(bodyDefault, statusDefault);
      }
      setCustomWebhooks(customWebhooks);
      setLinkedWebhooks(uniqueWebhookList);
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
      } else {
        toast.error(
          error.message ? error.message : t('ContentForm.CouldntFetch'),
        );
      }
    }
  }, [permissions, jwt, space, contentTypeName, t]);

  useOnce(fetchWebhooks);

  const fetchLinkedWebhooksLogs = useCallback(async () => {
    if (logsDisabled || linkedWebhooks.length == 0) {
      return [];
    }

    const logs = linkedWebhooks.map((webhook: Webhook) => {
      return logsWebhooks(jwt, space, { id: webhook.id, limit: 100 });
    });

    try {
      const response = await Promise.all(logs);
      const responseData = response.map(({ body }) => body.data).flat();
      const parsedLogs = responseData.reduce(
        (
          previousValue: ParsedLinkedWebhookLogsType,
          currentValue: WebhookLog,
        ) => {
          if (previousValue?.[currentValue.webhookId] === undefined) {
            previousValue[currentValue.webhookId] = [currentValue];
            return previousValue;
          }

          previousValue[currentValue.webhookId].push(currentValue);
          return previousValue;
        },
        {},
      );
      setLinkedWebhooksLogs(parsedLogs);
    } catch (e) {
      const error =
        (e as Error).message || t('ContentForm.LinkedWebhooksLogsFetchFailed');
      toast.error(error);
    }
  }, [jwt, linkedWebhooks, logsDisabled, space, t]);

  useOnce(fetchLinkedWebhooksLogs);

  const handleLinkedWebhooksRefresh = useCallback(async () => {
    await fetchWebhooks();
    await fetchLinkedWebhooksLogs();
  }, [fetchLinkedWebhooksLogs, fetchWebhooks]);

  const handleOpenWebhookLogsModal = async (
    webhookId: string,
  ): Promise<void> => {
    await modal({
      id: 'webhook-logs',
      size: 'lg',
      title: t('Webhooks.Logs'),
      contentAdditionalClasses: 'overflow-hidden',
      content: (
        <div className="md:min-h-[50vh] relative">
          <WebhookLogsContainer
            loading={false}
            additionalContainerClasses="before:mx-[1.4rem]! md:before:mx-[1.2rem]! 2xl:before:mx-5!"
            webhookLogs={linkedWebhooksLogs[webhookId]}
            {...getTestProps(testId, 'linked-webhooks-logs-modal', 'testId')}
          />
        </div>
      ),
      buttons: [
        {
          label: t('Global.Close'),
          key: 'close',
          onClick: () => null,
        },
      ],
    });
  };

  const handleEditWebhook = async (webhookId: string): Promise<void> => {
    await modal({
      id: 'editWebhook',
      size: 'page',
      hideClose: true,
      content: (
        <AddWebhooksContainer
          webhookId={webhookId}
          mode="edit"
          duplicate={false}
          testId={testId}
        />
      ),
    });
  };

  const handleDeleteObject = useCallback(async () => {
    modal.deleting('delete-modal');
    try {
      const { body, status, ok } = await deleteContentObject({
        contentTypeName,
      });
      if (!ok) {
        throw parseResponseErrors(body, status);
      }
      toast.success(t('ContentForm.Deleted'));

      setDirty(false);
      if (!inModal) navigateGrid?.();
      reloadConstraint();
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
      } else {
        toast.error(
          error.message ? error.message : t('ContentForm.CouldntDelete'),
        );
      }
    }
  }, [
    modal,
    deleteContentObject,
    contentTypeName,
    t,
    setDirty,
    inModal,
    navigateGrid,
    reloadConstraint,
  ]);

  const deleteObject = useCallback(async () => {
    setIsDeleting(true);
    await modal.delete(t('ContentForm.ConfirmDelete'), 'delete-modal', () =>
      handleDeleteObject(),
    );
    setIsDeleting(false);
  }, [handleDeleteObject, modal, t]);

  const {
    entity: apiContentType,
    errors: contentTypeErrors,
    status: contentTypeStatus,
    isLoading: contentTypeLoading,
  } = useContentType(contentTypeName);

  const { formContentType: contentType, isContentTypeOutdated } =
    useContentTypeRefresh({
      apiContentType,
      contentObject,
      contentTypeName,
      form: {
        state: formState,
        setState: setFormState,
        setUniqueKey: setFormUniqueKey,
      },
      collaboration: {
        yjsDocument,
        wsStatus,
        contentTypeUpdate: collaborationContentTypeUpdate,
      },
      mode: {
        id,
        duplicate,
      },
    });

  const { onDuplicate } = useDuplicateModal(contentType, testId);

  const firstLoading = useFirstLoading(
    (!contentTypeLoading || !contentTypeName) &&
      (!contentObjectIsLoading || !id),
    '' + contentTypeName + id,
    () =>
      setFormUniqueKey(
        contentObject?.internal?.latestVersion?.toString() || 'current',
      ),
  );

  useApiErrorsToast(contentTypeErrors);
  useApiErrorsToast(contentVersionsErrors);
  useApiErrorsToast(contentObjectErrors);

  const { canCreate, canDelete, canUpdate, canRead } = useMemo(
    () => permissions.getCoPermissions(contentTypeName) || {},
    [contentTypeName, permissions],
  );

  const notAllowed = useMemo(
    () => (contentType?.internal && contentType?.name !== '_tag') || !canRead,
    [contentType, canRead],
  );

  const label = useMemo(
    () =>
      contentType?.name === '_tag' ? t('Global.Tag') : contentType?.label || '',
    [contentType?.label, contentType?.name, t],
  );

  const pageTitle = useMemo(() => {
    if (duplicate) return 'Duplicate';
    else if (id) return 'Edit';
    return 'Add';
  }, [id, duplicate]);

  const afterSubmit = (
    contentObject: ContentObject,
    errors: Record<string, string | string[]>,
    hasErrors: boolean,
    formValues: WithoutInternalProps<ContentObject>,
  ) => {
    onSubmit?.({
      action: 'save',
      contentObject: contentObject,
      contentType,
      errors: errors,
      success: !hasErrors,
    });

    if (!hasErrors && contentObject?.id && contentTypeName) {
      sendRefetchSignalToWsServer(contentTypeName, contentObject.id);

      if (yjsDocument && formValues && contentObject && contentType) {
        yjsDocument.transact(() => {
          // Update yjs data when returned object is different than current form values,
          // e.g. when sync webhooks modifies data on submit
          Object.keys(contentObject).forEach((fieldName) => {
            const isSame = deepEqual(
              contentObject[fieldName],
              formValues[fieldName],
            );
            if (!isSame) {
              syncCollaborativeFieldValue(
                contentType,
                fieldName,
                contentObject[fieldName],
                yjsDocument,
              );
            }
          });
        });
      }
    }

    if (navigateOnSave.current && !hasErrors) {
      if (inModal) {
        modalInstance.resolve?.(contentObject);
      } else {
        navigateGrid();
      }
      return true;
    }

    return false;
  };

  const handleSyncWebhookErrors = useCallback(
    (errors: Record<string, string | string[]>) => {
      if (errors && typeof errors === 'object' && '__webhook' in errors) {
        errors['__webhook.response.message'] = errors['__webhook'];
        delete errors['__webhook'];

        setSyncWebhookErrorLogs(unflatten(errors));
      }
    },
    [],
  );

  const saveNewObject: ContentObjectSubmitFn = async (values) => {
    setIsSaving(true);
    setSyncWebhookErrorLogs({});
    const [newObject, errors, hasErrors] = await saveNewCTO(
      jwt,
      space,
      values,
      contentTypeName,
      user,
      setUser,
      t,
    );
    setIsSaving(false);

    const navigated = afterSubmit(newObject, errors, hasErrors, values);
    if (hasErrors) {
      handleSyncWebhookErrors(errors);
    }
    if (!hasErrors) {
      reloadConstraint();

      if (!inModal && !navigated) {
        navigate(
          buildUrlWithSpace(
            `content-type-objects/edit/${contentTypeName}/${newObject.id}`,
          ),
        );
      }
    }

    lastPanelSaveResult.current = [newObject, errors, hasErrors];

    return [newObject, errors, hasErrors];
  };

  const updateObject: ContentObjectSubmitFn = async (
    values: WithoutInternalProps<ContentObject>,
    disableLoader: boolean = false,
  ) => {
    if (!disableLoader) setIsSaving(true);
    setSyncWebhookErrorLogs({});

    const [contentObject, errors, hasErrors] = await updateCTO(
      values,
      contentTypeName,
      permissions.hasCoLimitedFields(contentTypeName)
        ? partialUpdateContentObject
        : updateContentObject,
      t,
      disableLoader,
    );

    await reloadContentVersions();
    if (!disableLoader) setIsSaving(false);
    if (hasErrors) {
      handleSyncWebhookErrors(errors);
    }
    afterSubmit(contentObject, errors, hasErrors, values);

    if (yjsDocument)
      yjsDocument
        .getMap('formState')
        .set('latestVersion', contentObject.internal?.latestVersion);

    lastPanelSaveResult.current = [contentObject, errors, hasErrors];

    return [contentObject, errors, hasErrors];
  };

  const handleVersionRestore = useCallback(
    async (oldContentObject: WithOptionalInternalProps<ContentObject>) => {
      delete oldContentObject.id;
      delete oldContentObject.internal;

      if (formState) {
        formState.setValues(oldContentObject);
        await formState.submitForm();
        contentObjectReload();
      }
    },
    [contentObjectReload, formState],
  );

  const handleSidePanelSave =
    useCallback(async (): Promise<ContentObject | null> => {
      if (!formState) return null;

      lastPanelSaveResult.current = null;
      await formState.submitForm();

      const result = lastPanelSaveResult.current as SidePanelSaveResult | null;
      if (!result) return null;

      const [object, , hasErrors] = result;
      return hasErrors ? null : object;
    }, [formState]);

  const openSyncWebhookErrorsModal = useCallback(async () => {
    await modal({
      title: t('Webhooks.SyncErrorLogs'),
      size: 'lg',
      content: (
        <SyncWebhookErrorLogsModalContent
          webhookErrors={syncWebhookErrorLogs}
          testId={testId}
        />
      ),
      buttons: [
        {
          key: 'ok',
          label: t('Global.Ok'),
          result: true,
        },
      ],
    });
  }, [modal, syncWebhookErrorLogs, t, testId]);

  const emptyCTD = useMemo(() => {
    if (firstLoading || waitForYjsSync) {
      return (
        <Loader
          size={'small'}
          type={'spinner-grid'}
          testId={testId ? `${testId}-loader` : ''}
        />
      );
    }
    if (notAllowed) {
      toast.error(t('ContentForm.CouldntFind', { contentTypeName: label }));
    }
    return (
      <Heading
        level={2}
        additionalClasses={twMerge(
          'text-3xl md:text-4xl leading-8 dark:text-slate-200',
        )}
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon
            className="text-red w-14 md:w-20 mb-3"
            title={t('ContentForm.CouldntFind')}
          />
          {contentTypeStatus === 404 || notAllowed
            ? t('ContentForm.CouldntFind', { contentTypeName })
            : t('ContentForm.CouldntFetch')}
        </div>
      </Heading>
    );
  }, [
    firstLoading,
    waitForYjsSync,
    notAllowed,
    testId,
    t,
    contentTypeStatus,
    contentTypeName,
    label,
  ]);

  const currentStatus = contentObject?.internal?.status;
  const draftPublic = contentType?.draftPublic;

  const handlePublish = async () => {
    if (editorsWithActiveField && editorsWithActiveField.length > 0) {
      const publishNow = await modal({
        title: defaultTextRender.simple('Some fields are still being edited'),
        content:
          // eslint-disable-next-line @stylistic/max-len
          'One or more fields are currently being edited by another user. Publishing now will exclude those changes.\n\nWould you like to continue?',
        buttons: [
          {
            ...defaultButtonsProps.cancel,
            label: t('Global.Cancel'),
            result: false,
          },
          {
            key: 'publish',
            label: 'Publish anyway',
            result: true,
            color: 'blue',
            iconPosition: 'start',
            iconImage: <DownloadCircleIcon className="w-3 min-w-3" />,
          },
        ],
      });
      if (!publishNow) return;
    }

    if (contentObject?.internal?.status === 'scheduled') {
      const publishNow = await modal({
        title: defaultTextRender.warning(t('Global.Warning')),
        content: t('PublicationSchedule.AlreadyScheduled'),
        buttons: [
          {
            ...defaultButtonsProps.cancel,
            label: t('Global.Cancel'),
            result: false,
          },
          {
            key: 'publish',
            label: t('ObjectStatus.Transition.publish'),
            result: true,
            color: 'blue',
            iconPosition: 'start',
            iconImage: <DownloadCircleIcon className="w-3 min-w-3" />,
          },
        ],
      });
      if (!publishNow) return;
    }

    updateStatuses({
      objects: { [contentTypeName!]: [id!] },
      action: 'publish',
    });
  };

  const handleSchedule = useCallback(async () => {
    const values = await modal({
      title: (
        <h2 className="font-medium text-3xl">
          {t('PublicationSchedule.SetSchedule')}
        </h2>
      ),
      content: (
        <SchedulePublicationForm
          initialData={
            currentStatus === 'scheduled' ? contentObject?.internal : undefined
          }
          {...getTestProps(testId, 'schedule-modal', 'testId')}
        />
      ),
      dialogAdditionalClasses: 'overflow-visible',
      contentAdditionalClasses: 'overflow-visible',
    });

    if (values) {
      updateStatuses({
        objects: { [contentTypeName!]: [id!] },
        action: 'schedule',
        values: { scheduledAt: values.datetime, timeZone: values.timezone },
      });
    }
  }, [
    modal,
    t,
    currentStatus,
    contentObject?.internal,
    testId,
    updateStatuses,
    contentTypeName,
    id,
  ]);

  const onScheduleDelete = useCallback(async () => {
    const result = await modal.confirmation(
      t('PublicationSchedule.RemoveConfirm'),
      t('Global.Warning'),
      t('PublicationSchedule.Remove'),
    );

    if (result) {
      updateStatuses({
        objects: { [contentTypeName!]: [id!] },
        action: 'unschedule',
      });
    }
  }, [modal, t, updateStatuses, contentTypeName, id]);

  const handleUnpublish = useCallback(() => {
    updateStatuses({
      objects: { [contentTypeName!]: [id!] },
      action: 'unpublish',
    });
  }, [updateStatuses, contentTypeName, id]);

  const handleArchive = useCallback(() => {
    updateStatuses({
      objects: { [contentTypeName!]: [id!] },
      action: 'archive',
    });
  }, [updateStatuses, contentTypeName, id]);

  const handleShowSidebar = useCallback(() => {
    setShowSidebar(!showSidebar);
  }, [showSidebar]);

  const isFormDisabled = useMemo(() => {
    if (isContentTypeOutdated) return true;
    if (isSaving || isDeleting) return true;
    if (id) return !canUpdate;
    return !canCreate;
  }, [canCreate, canUpdate, id, isContentTypeOutdated, isDeleting, isSaving]);

  const eventParams = useMemo(
    () => ({
      contentType,
      contentObject,
      disabled: isFormDisabled,
      duplicate,
      create: !id || duplicate,
      form: formState,
      formUniqueKey,
    }),
    [
      contentObject,
      contentType,
      duplicate,
      formState,
      formUniqueKey,
      id,
      isFormDisabled,
    ],
  );

  /**
   * @emits FlotiqPlugins."flotiq.form.sidebar-panel::add"
   */
  const pluginSidebarPanels = usePluginResults(
    'flotiq.form.sidebar-panel::add',
    FormAddSidebarPanelEvent,
    eventParams,
  );

  /**
   * @emits FlotiqPlugins."flotiq.form.secondary-column::add"
   */
  const pluginSecondaryColumn = usePluginResults(
    'flotiq.form.secondary-column::add',
    FormAddSecondaryColumnEvent,
    eventParams,
  );

  const isSecondaryColumn = pluginSecondaryColumn?.length > 0;

  const hasUnpublishActionButton = useMemo(
    () =>
      id &&
      !duplicate &&
      draftPublic &&
      currentStatus &&
      ['public', 'modified'].includes(currentStatus),
    [id, currentStatus, duplicate, draftPublic],
  );

  const hasArchiveActionButton =
    id &&
    !duplicate &&
    draftPublic &&
    currentStatus &&
    currentStatus !== 'archived';

  const publishButtonDisabledReason = useMemo(() => {
    if (!id) {
      return t('ObjectStatus.Tooltip.PublishDisabledNotSaved');
    }
    if (currentStatus === 'public') {
      return t('ObjectStatus.Tooltip.PublishDisabledAlreadyPublished');
    }
    if (isDataDirty) {
      return t('ObjectStatus.Tooltip.PublishDisabledFormChanged');
    }
  }, [id, t, currentStatus, isDataDirty]);

  const topbarStatus = useMemo(() => {
    if (!id || !currentStatus || duplicate) return '';
    if (!draftPublic) return 'saved';
    return currentStatus;
  }, [id, currentStatus, duplicate, draftPublic]);

  const topbarStatusTooltip = useMemo(() => {
    if (topbarStatus === 'saved') {
      return t('ObjectStatus.Tooltip.EnableDraftPublic');
    }
  }, [t, topbarStatus]);

  const isSaveAllowed = (canUpdate && id) || (canCreate && !id);
  const isSaveDisabled =
    isContentTypeOutdated || (!!id && !duplicate && !isDataDirty);

  const isWsBroken = wsStatus === 'failed' || wsStatus === 'retrying';
  const showStatusBar = !!topbarStatus || isWsBroken;

  const statusContent = (
    <>
      <FormEditorsIndicator
        editors={editors}
        {...getTestProps(testId, 'collaboration', 'testId')}
      />
      {isWsBroken && (
        <TopbarReconnectButton
          onRetry={retryWsConnection}
          isRetrying={wsStatus === 'retrying'}
        />
      )}
      {topbarStatus && (
        <TopbarStatus
          label={t(`ObjectStatus.Status.${topbarStatus}`)}
          status={topbarStatus}
          testId={testId}
          tooltip={topbarStatusTooltip}
        />
      )}
    </>
  );

  const onCancel = useCallback(() => {
    modalInstance.resolve?.(contentObject);
  }, [contentObject, modalInstance]);

  const LayoutComponent = inModal ? PoppingPageLayout : PageLayout;

  const columnResizerRef = useRef<Resizer.ColumnResizer>(null);

  useEffect(() => {
    if (isSecondaryColumn && columnResizerRef.current) {
      const resizer = columnResizerRef.current.getResizer();
      if (!resizer) return;

      const totalSize = resizer.getTotalSize();

      resizer.resizeSection(0, {
        toSize: 0.3 * totalSize,
      });
      columnResizerRef.current.applyResizer(resizer);
    }
  }, [isSecondaryColumn]);

  const showForm =
    !notAllowed && !firstLoading && !!contentType && !waitForYjsSync;

  return (
    <LayoutComponent
      {...(inModal
        ? ({
            onBack: onCancel,
          } as any)
        : {
            id:
              contentTypeName === '_tag'
                ? 'tags'
                : `contentTypeObjects-${mode}`,
            page:
              contentTypeName === '_tag'
                ? 'tags'
                : `content/${label || contentTypeName}`,
            menuItemOpen: contentTypeName === '_tag' ? '' : 'content',
            breadcrumbs: (
              <TopbarBreadcrumbs
                parentTitle={t('ObjectsOfType.Title', {
                  contentTypeName: label,
                })}
                parentLink={gridLink}
              />
            ),
            hideSidebar: isSecondaryColumn,
          })}
      title={t(`ContentForm.${pageTitle}`, { contentTypeName: label })}
      noPaddings={isSecondaryColumn}
      buttonsDisabled={
        isSaving || isDeleting || isTransiting || isContentTypeOutdated
      }
      buttons={
        <>
          <TopbarCancelButton
            isDirty={isDataDirty}
            {...(inModal ? { onClick: onCancel } : { link: gridLink })}
          />
          <TopbarSaveButton
            form={formId}
            isLoading={isSaving}
            label={draftPublic ? t('ObjectStatus.SaveDraft') : t('Global.Save')}
            disabled={isSaveDisabled}
            actionDisallowed={!isSaveAllowed}
            navigateOnSave={navigateOnSave}
          />
          {draftPublic && (
            <TopbarPublishButton
              onPublish={handlePublish}
              onSchedule={
                isPublicationPlanningDisabled ? undefined : handleSchedule
              }
              isLoading={isTransiting}
              disabled={!!publishButtonDisabledReason}
              tooltip={publishButtonDisabledReason}
            />
          )}
          <TopbarActionMenu>
            {id && !duplicate && !inModal && contentTypeName !== '_tag' && (
              <TopbarDuplicateButton
                actionDisallowed={!canCreate}
                onClick={() => onDuplicate(contentObject)}
              />
            )}
            <TopbarSaveAndLeaveButton
              form={formId}
              disabled={isSaveDisabled}
              actionDisallowed={!isSaveAllowed}
              navigateOnSave={navigateOnSave}
            />
            {draftPublic && (
              <TopbarPublishButton
                onPublish={handlePublish}
                onSchedule={handleSchedule}
                isLoading={isTransiting}
                disabled={!!publishButtonDisabledReason}
                actionButton={true}
                actionDisallowed={!canUpdate}
              />
            )}
            {hasUnpublishActionButton && (
              <TopbarActionButton
                color="red"
                label={t('ObjectStatus.Transition.unpublish')}
                iconImage={<ArrowLeftCurvedIcon className="w-3 min-w-3" />}
                onClick={handleUnpublish}
                actionDisallowed={!canUpdate}
                additionalIconClasses="ml-0.5 mr-3"
                testIdSuffix="unpublish"
              />
            )}
            {hasArchiveActionButton && (
              <TopbarActionButton
                color="red"
                label={t('ObjectStatus.Transition.archive')}
                iconImage={<BoxWithArrowIcon className="w-3 min-w-3" />}
                onClick={handleArchive}
                actionDisallowed={!canUpdate}
                additionalIconClasses="ml-0.5 mr-3"
                testIdSuffix="archive"
              />
            )}
            {id && !duplicate && !inModal && (
              <TopbarDeleteButton
                onClick={deleteObject}
                actionDisallowed={!canDelete}
              />
            )}
          </TopbarActionMenu>
        </>
      }
      status={statusContent}
      testId={testId}
    >
      {showForm ? (
        <Resizer.Container
          className={twMerge(
            'relative',
            predefinedLayoutClasses.withSidebar,
            isSecondaryColumn ? 'py-4 lg:py-7 gap-0 lg:gap-0' : 'grid!',
          )}
          onActivate={toggleIsResizing}
          afterResizing={toggleIsResizing}
          columnResizerRef={columnResizerRef}
          {...getTestProps(testId, 'resizer-container')}
        >
          <Resizer.Section
            className={twMerge(
              predefinedLayoutClasses.leftColumnWhite,
              'relative',
              !showSidebar && 'xl:col-span-7 xl:mr-16',
              'overflow-visible!',
            )}
            minSize={50}
            {...getTestProps(testId, 'resizer-form-section')}
          >
            <div className="space-y-3 md:space-y-6 p-7 md:py-10 md:px-14">
              {!isSyncWebhookErrorLogsModalDisabled &&
                Object.keys(syncWebhookErrorLogs).length > 0 && (
                  <SyncWebhookErrorLogs
                    openSyncWebhookLogModal={openSyncWebhookErrorsModal}
                    testId={testId}
                  />
                )}
              <WebsocketContext.Provider
                value={{ yjsDocument, awareness, editors, isYjsDirty }}
              >
                <ContentObjectForm
                  key={`${pageTitle}-${formUniqueKey}`}
                  formId={formId}
                  contentType={contentType}
                  contentObject={contentObject}
                  isEditing={!!(id && !duplicate)}
                  onSubmit={id && !duplicate ? updateObject : saveNewObject}
                  disabled={isFormDisabled}
                  formUniqueKey={formUniqueKey}
                  setFormState={setFormState}
                  statusBar={
                    showStatusBar ? (
                      <div
                        className="flex items-center gap-3 lg:hidden"
                        {...getTestProps(testId, 'form-status-bar')}
                      >
                        {statusContent}
                      </div>
                    ) : undefined
                  }
                  {...getTestProps(testId, 'form', 'testId')}
                />
              </WebsocketContext.Provider>
            </div>
            <Button
              onClick={handleShowSidebar}
              iconImage={
                showSidebar ? (
                  <ArrowCollapseRightIcon className="w-5 h-5 text-blue" />
                ) : (
                  <ArrowCollapseLeftIcon className="w-5 h-5 text-blue" />
                )
              }
              additionalIconClasses={'min-w-10 w-10'}
              buttonColor="borderless"
              additionalClasses={twMerge(
                'hidden absolute top-2.5 right-1.5',
                !isSecondaryColumn &&
                  getEnv('VITE_ENABLE_CTO_FORM_RESIZE') === 'true' &&
                  'xl:flex',
              )}
              {...getTestProps(testId, 'toggle-sidebar', 'testId')}
            />
          </Resizer.Section>

          <Resizer.Bar
            size={28}
            className={twMerge(
              !isSecondaryColumn && 'hidden',
              'relative cursor-col-resize',
            )}
            {...getTestProps(testId, 'resizer-bar')}
          >
            <ResizeIcon className="w-1.5 h-4 fixed top-1/2 translate-x-3 -translate-y-1/2" />
          </Resizer.Bar>

          <Resizer.Section
            minSize={50}
            className={twMerge(
              !isSecondaryColumn && 'hidden',
              isResizing
                ? 'pointer-events-none overflow-visible!'
                : 'overflow-visible!',
            )}
            {...getTestProps(testId, 'resizer-secondary-section')}
          >
            <ElementFromPlugin results={pluginSecondaryColumn} />
          </Resizer.Section>

          <div
            className={twMerge(
              predefinedLayoutClasses.rightColumn,
              'xl:sticky xl:top-20 w-full h-fit xl:max-h-[calc(100vh-106px)]',
              'xl:overflow-y-auto xl:pr-2 scrollbar-sm',
              !showSidebar &&
                'xl:fixed xl:right-0 xl:w-16 xl:top-22 xl:p-0 xl:h-full xl:overflow-visible',
              isSecondaryColumn && 'hidden',
            )}
            {...getTestProps(testId, 'sidebar-container')}
          >
            <ContentPreviewPanel
              contentTypeName={contentTypeName}
              contentObject={contentObject}
              create={!id || duplicate}
              duplicate={duplicate}
              hidden={!showSidebar}
              draftPublic={draftPublic}
              isDirty={isDataDirty}
              onPanelSave={handleSidePanelSave}
              testId={testId}
            />

            <CustomLinksPanel
              contentTypeName={contentTypeName}
              contentObject={contentObject}
              create={!id || duplicate}
              duplicate={duplicate}
              hidden={!showSidebar}
              testId={testId}
            />

            {!duplicate &&
              currentStatus === 'scheduled' &&
              !isPublicationPlanningDisabled && (
                <ContentObjectPublicationSchedule
                  publishedAt={contentObject?.internal?.publishedAt}
                  onEdit={handleSchedule}
                  onDelete={onScheduleDelete}
                  isPinned={!showSidebar}
                  additionalClasses="-order-1"
                  {...getTestProps(testId, 'schedule', 'testId')}
                />
              )}
            {!duplicate && (
              <ContentObjectInformation
                createdAt={contentObject?.internal?.createdAt}
                updatedAt={contentObject?.internal?.updatedAt}
                updatedAtText={
                  draftPublic ? t('TypeDefinitionCard.LastUpdated') : undefined
                }
                lastPublishedAt={contentObject?.internal?.publishedAt}
                additionalClasses={twMerge('order-20')}
                isPinned={!showSidebar}
                testId={testId}
                id={'content_object-information'}
              />
            )}
            {customWebhooks.length > 0 && !duplicate && id && (
              <ContentObjectWebhooks
                webhooks={customWebhooks}
                contentObjectId={id}
                contentTypeDefinitionName={contentTypeName}
                additionalClasses={twMerge(
                  'order-30',
                  !showSidebar && 'xl:hidden',
                )}
                testId={testId}
              />
            )}
            {id && !duplicate && linkedWebhooks.length > 0 && !logsDisabled && (
              <LinkedWebhooks
                webhooks={linkedWebhooks}
                logs={linkedWebhooksLogs}
                handleRefreshCallback={handleLinkedWebhooksRefresh}
                handleOpenWebhookLogsModal={handleOpenWebhookLogsModal}
                handleEditWebhook={handleEditWebhook}
                additionalContainerClasses="order-30"
                testId={testId}
              />
            )}
            {id && ctoVersions?.length > 0 && !duplicate && (
              <ContentObjectVersions
                objectId={id}
                versions={getSortedBy(ctoVersions, 'version')}
                onRestore={handleVersionRestore}
                currentChanges={formState?.getValues() as Record<string, any>}
                isDirty={isDataDirty}
                contentType={contentType}
                additionalClasses={twMerge(
                  'order-40',
                  !showSidebar && 'xl:hidden',
                )}
                testId={testId}
              />
            )}
            {!duplicate && backlinks?.length > 0 && (
              <ContentObjectBacklinks
                backlinks={backlinks}
                contentTypes={backlinkContentTypes}
                pagination={backlinkPagination}
                onPageChange={setBacklinksPage}
                additionalClasses={twMerge(
                  'order-50',
                  !showSidebar && 'xl:hidden',
                )}
              />
            )}

            <PlainObjectEditor
              onSubmit={id && !duplicate ? updateObject : saveNewObject}
              defaultValue={
                contentObject && !duplicate
                  ? JSON.stringify(contentObject, null, 2)
                  : ''
              }
              formState={formState}
              additionalClasses={twMerge(
                'order-60',
                !showSidebar && 'xl:hidden',
              )}
              {...getTestProps(testId, 'plain-object-editor', 'testId')}
            />
            {pluginSidebarPanels?.length > 0 && showSidebar && (
              <ElementFromPlugin results={pluginSidebarPanels} />
            )}
          </div>
        </Resizer.Container>
      ) : (
        <div className={predefinedLayoutClasses.whiteBox}>{emptyCTD}</div>
      )}
    </LayoutComponent>
  );
};

export default AddContentObjectContainer;
